/* Drawer enhancement only. No cart-page / checkout mutations. */
(() => {
  if (window.lisadoCartDrawerInstalled) return;
  window.lisadoCartDrawerInstalled = true;
  const selector = 'cart-drawer.lisado-cart-drawer';
  const drawer = () => document.querySelector(selector);
  const cartPath = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  let refreshNumber = 0;
  let lastScroll = 0;
  let lastShippingFill = null;
  let lastShippingProgress = null;
  let shippingFrame = 0;

  function syncShipping(root) {
    const shipping = root.querySelector('.lisado-shipping[data-lisado-shipping-threshold]');
    const track = shipping?.querySelector('[role="progressbar"]');
    const fill = track?.querySelector('[data-lisado-shipping-fill]');
    if (!shipping || !track || !fill) {
      if (shippingFrame) window.cancelAnimationFrame(shippingFrame);
      shippingFrame = 0;
      lastShippingFill = null;
      lastShippingProgress = null;
      return;
    }
    const total = Number(shipping.dataset.lisadoShippingTotal);
    const threshold = Number(shipping.dataset.lisadoShippingThreshold);
    if (!Number.isFinite(total) || !Number.isFinite(threshold) || threshold <= 0) return;
    const progress = Math.round(Math.min(100, Math.max(0, total / threshold * 100)) * 100) / 100;
    const rounded = String(progress >= 100 ? 100 : Math.min(99, Math.round(progress)));
    track.setAttribute('aria-valuenow', rounded);
    const percentage = shipping.querySelector('[data-lisado-shipping-percentage]');
    if (percentage && percentage.textContent !== `${rounded} %`) percentage.textContent = `${rounded} %`;
    shipping.classList.toggle('lisado-shipping--complete', total >= threshold);

    if (fill !== lastShippingFill || progress !== lastShippingProgress) {
      if (shippingFrame) window.cancelAnimationFrame(shippingFrame);
      shippingFrame = 0;
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (fill !== lastShippingFill && lastShippingProgress !== null && progress !== lastShippingProgress &&
          root.classList.contains('active') && !reducedMotion && typeof window.requestAnimationFrame === 'function') {
        // Native Shopify updates replace the bar: start the new bar at its previous value.
        fill.style.setProperty('width', `${lastShippingProgress}%`, 'important');
        fill.getBoundingClientRect();
        shippingFrame = window.requestAnimationFrame(() => {
          if (fill.isConnected) fill.style.setProperty('width', `${progress}%`, 'important');
          shippingFrame = 0;
        });
      } else {
        fill.style.setProperty('width', `${progress}%`, 'important');
      }
      lastShippingFill = fill;
      lastShippingProgress = progress;
    }
  }

  function sync() {
    const root = drawer();
    if (!root) return;
    syncShipping(root);
    const count = Number(root.querySelector('[data-lisado-item-count]')?.dataset.lisadoItemCount || 0);
    if (root.classList.contains('is-empty') !== (count === 0)) root.classList.toggle('is-empty', count === 0);
    // Standard quantity changes replace .drawer__inner. Restore focus trap to new nodes.
    if (root.classList.contains('active') && !root.contains(document.activeElement)) {
      const target = count === 0 ? root.querySelector('.drawer__inner-empty a') : root.querySelector('.drawer__inner');
      if (target && typeof trapFocus === 'function') trapFocus(root.querySelector('.drawer__inner'), target);
    }
  }

  function render(html, {focusCheckout = false} = {}) {
    const root = drawer();
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    const target = root?.querySelector('.drawer__inner');
    if (!source || !target) throw new Error('Der Warenkorb konnte nicht aktualisiert werden. Bitte lade die Seite neu.');
    const focusedId = root.contains(document.activeElement) ? document.activeElement.id : '';
    const scroll = root.querySelector('cart-drawer-items')?.scrollTop || lastScroll;
    target.replaceWith(source);
    sync();
    const scroller = root.querySelector('cart-drawer-items');
    if (scroller) scroller.scrollTop = scroll;
    if (root.classList.contains('active') && typeof trapFocus === 'function') {
      const focusTarget = focusCheckout ? root.querySelector('#CartDrawer-Checkout') :
        (focusedId ? document.getElementById(focusedId) : null) || root.querySelector('.drawer__inner');
      trapFocus(root.querySelector('.drawer__inner'), focusTarget);
    }
  }

  async function refresh(options) {
    const requestNumber = ++refreshNumber;
    const url = new URL(cartPath(), window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    const response = await fetch(url, {headers: {'Accept': 'text/html'}, cache: 'no-store'});
    if (!response.ok) throw new Error('Der Warenkorb konnte nicht aktualisiert werden. Bitte lade die Seite neu.');
    const html = await response.text();
    if (requestNumber === refreshNumber) render(html, options);
  }

  function busy(root, value) {
    root.toggleAttribute('data-lisado-busy', value);
    const items = root.querySelector('cart-drawer-items');
    if (items) items.inert = value;
    root.querySelector('.drawer__inner')?.setAttribute('aria-busy', String(value));
    if (value) {
      root.querySelectorAll('button:not(.drawer__close), input.quantity__input').forEach(control => {
        if (!control.disabled) { control.dataset.lisadoUnlock = ''; control.disabled = true; }
      });
    } else {
      root.querySelectorAll('[data-lisado-unlock]').forEach(control => {
        control.disabled = false;
        delete control.dataset.lisadoUnlock;
      });
    }
  }

  if (!customElements.get('lisado-cart-upsell')) {
    customElements.define('lisado-cart-upsell', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.addEventListener('submit', async event => {
          event.preventDefault();
          event.stopPropagation();
          const root = drawer();
          if (!root || root.hasAttribute('data-lisado-busy')) return;
          const form = event.target;
          const button = form.querySelector('button');
          const originalText = button.textContent;
          const feedback = root.querySelector('[data-lisado-upsell-feedback]');
          feedback.removeAttribute('data-error');
          feedback.textContent = '';
          button.textContent = 'Wird hinzugefügt …';
          busy(root, true);
          let added = false;
          let requestSent = false;
          let responseReceived = false;
          try {
            const data = new FormData(form);
            requestSent = true;
            const response = await fetch(window.routes?.cart_add_url || `${cartPath()}/add`, {
              method: 'POST',
              headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
              body: JSON.stringify({
                items: [{id: Number(data.get('id')), quantity: 1}],
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname
              })
            });
            const state = await response.json();
            responseReceived = true;
            if (!response.ok || state.status) throw new Error(state.description || 'Hinzufügen nicht möglich. Bitte versuche es erneut.');
            added = true;
            // Null section HTML does not mean the add failed. Never retry the POST.
            const html = state.sections?.['cart-drawer'];
            if (html) render(html, {focusCheckout: true});
            else await refresh({focusCheckout: true});
            const iconHTML = state.sections?.['cart-icon-bubble'];
            const icon = document.getElementById('cart-icon-bubble');
            const iconSource = iconHTML && new DOMParser().parseFromString(iconHTML, 'text/html').querySelector('.shopify-section');
            if (icon && iconSource) icon.innerHTML = iconSource.innerHTML;
            busy(root, false);
            const message = root.querySelector('[data-lisado-upsell-feedback]') || root.querySelector('#CartDrawer-LiveRegionText');
            if (message) { message.removeAttribute('aria-hidden'); message.textContent = 'Produkt hinzugefügt. Deine Auswahl wurde aktualisiert.'; }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              root.setAttribute('data-lisado-publishing', '');
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {source: 'lisado-drawer-upsell', productVariantId: data.get('id'), cartData: state}).catch(() => {});
              } finally {
                root.removeAttribute('data-lisado-publishing');
              }
            }
          } catch (error) {
            const message = root.querySelector('[data-lisado-upsell-feedback]') || feedback;
            message.setAttribute('data-error', '');
            if (added || (requestSent && !responseReceived)) {
              // Prevent duplicate purchases if adding succeeded but rendering failed.
              message.textContent = added ? 'Produkt hinzugefügt. Bitte lade die Seite neu, um den aktuellen Warenkorb zu sehen.' : 'Die Verbindung wurde unterbrochen. Bitte lade die Seite neu und prüfe deinen Warenkorb, bevor du erneut hinzufügst.';
              button.textContent = added ? 'Bereits hinzugefügt' : 'Warenkorb prüfen';
              delete button.dataset.lisadoUnlock;
              button.disabled = true;
            } else {
              message.textContent = error.message || 'Bitte prüfe deine Verbindung und versuche es erneut.';
              button.textContent = originalText;
            }
          } finally {
            busy(root, false);
          }
        });
      }
    });
  }

  customElements.whenDefined('cart-drawer-items').then(() => {
    const Items = customElements.get('cart-drawer-items');
    const original = Items.prototype.onCartUpdate;
    Items.prototype.onCartUpdate = function () {
      if (!this.closest(selector)) return original.call(this);
      if (drawer()?.hasAttribute('data-lisado-publishing')) return Promise.resolve();
      return refresh().catch(() => {
        const errors = drawer()?.querySelector('#CartDrawer-CartErrors');
        if (errors) errors.textContent = 'Bitte lade die Seite neu, um den aktuellen Warenkorb zu sehen.';
      });
    };
  });

  const root = drawer();
  if (!root) return;
  root.addEventListener('click', event => {
    // Also works after existing product-page scripts replace the complete drawer HTML.
    if (event.target.id === 'CartDrawer-Overlay') root.close();
    lastScroll = root.querySelector('cart-drawer-items')?.scrollTop || 0;
  });
  const observer = new MutationObserver(sync);
  observer.observe(root, {childList: true, subtree: true});
  sync();
})();
